import { stringify } from 'query-string';
import { template } from 'lodash';
import axios from './client';

const CONTRACTOR_URL = template('/contractor/<%= id %>');
const MANAGE_GET_CONTRACTORS = '/manage/contractors';
const MANAGE_CONTRACTORS_URL = '/manage/contractor';
const SETTINGS_LEAD_SOURCES = '/setting/lead-sources';
const MANAGE_LEAD = '/manage/lead';
const MANAGE_LEAD_MERGE = '/manage/lead/merge';
const MANAGE_CONTRACTOR_URL = template('/manage/contractor/<%= id %>');
const CONTRACTOR_PAYOUT_TAX = template('/contractor/<%= id %>/payout-tax');
const CONTRACTOR_W9 = template('/contractor/<%= id %>/w9');
const TEMPLATE_W9_PDF_URL = '/template/w9/pdf';
const CONTRACTOR_W9_PDF = template('/manage/contractor/<%= id %>/w9');
const CONTRACTOR_PAGE = template('/contractor/<%= id %>/page');
const CONTRACTOR_PAGE_UPDATE = template('/contractor/<%= id %>/page/<%= pid %>');
const CONTRACTOR_PAGE_UPDATE_MARKETING_MATERIALS = template('/contractor/<%= id %>/page/<%= pid %>/marketing-materials');
const CONTRACTOR_PAGE_SETTINGS = '/contractor/page/settings';
const REFERRAL_URL = template('/contractor/<%= id %>/refer');
const CONTRACTOR_QUIZ = template('/contractor/<%=id %>/quiz');
const CONTRACTOR_CO_BRANDED_ANALYTICS = template('/contractor/<%= id %>/co-branded/analytics');
const CONTRACTOR_TEAM_MAX = template('/contractor/<%= id %>/team/max');
const CONTRACTOR_TEAM_USERS = template('/contractor/<%= id %>/team');
const CONTRACTOR_TEAM_RESOURCES = template('/contractor/<%= id %>/team/resources');
const CONTRACTOR_TEAM_USER_CREATE = template('/contractor/<%= contractorId %>/team/user');
const CONTRACTOR_TEAM_USER_UPDATE = template('/contractor/<%= contractorId %>/team/user/<%= userId %>');
const CONTRACTOR_TEAM_USER_DELETE = template('/contractor/<%= contractorId %>/team/user/<%= userId %>');
const CONTRACTOR_CONNECTED_ACCOUNT = template('/contractor/<%= contractorId %>/connected-account');
const CONTRACTOR_REFRESH_CONNECTED_ACCOUNT = template('/contractor/<%= contractorId %>/connected-account/refresh');
const CONTRACTOR_SUCCESS_CONNECTED_ACCOUNT = template('/contractor/<%= contractorId %>/connected-account/success');

export function getContractorCoBrandedPages(contractorId) {
  return axios.get(`/contractor/${contractorId}/co-branded-pages/filter-list`);
}

/**
 * Get all contractors
 * @returns {Promise}
 */
export function contractors(params = {}) {
  return axios.get(MANAGE_CONTRACTORS_URL, { params });
}

export function getContractors(params = {}) {
  return axios.get(MANAGE_GET_CONTRACTORS, { params });
}

/**
 * Get contractor by id
 * @param id
 * @returns {Promise}
 */
export function contractor(id) {
  return axios.get(CONTRACTOR_URL({ id }));
}

export function getLeadSources() {
  return axios.get(`${SETTINGS_LEAD_SOURCES}?show=true`);
}

export function createLead(payload) {
  const data = stringify(payload, { arrayFormat: 'bracket' });
  return axios.post(MANAGE_LEAD, data);
}

export function mergeLeads(payload) {
  return axios.post(MANAGE_LEAD_MERGE, payload);
}

/**
 * get W-9 PDF from server
 * @returns {Promise}
 */
export function getTemplateW9Pdf() {
  return axios.get(TEMPLATE_W9_PDF_URL);
}

/**
 * get exist W-9 PDF from server
 * @returns {Promise}
 */
export function getExistW9Pdf(id) {
  return axios.get(CONTRACTOR_W9_PDF({ id }));
}

/**
 * save W-9 PDF to server
 * @param formData
 * @returns {Promise}
 */
export function saveW9PDF(formData) {
  const id = formData.get('id');
  const headers = { 'Content-Type': 'multipart/form-data' };
  return axios.post(CONTRACTOR_W9({ id }), formData, { headers });
}

/**
 * update W-9 status to server
 * @param formData
 * @returns {Promise}
 */
export function updateW9Status(formData) {
  const id = formData.get('id');
  const headers = { 'Content-Type': 'multipart/form-data' };
  return axios.post(CONTRACTOR_W9_PDF({ id }), formData, { headers });
}

/**
 * Get contractor's w9 info
 * @param id
 * @returns {Promise}
 */
export function contractorW9(id) {
  return axios.get(CONTRACTOR_W9({ id }));
}

/**
 * Get contractor's payout method
 * @param id
 * @returns {Promise}
 */
export function contractorPayoutTax(id) {
  return axios.get(CONTRACTOR_PAYOUT_TAX({ id }));
}

/**
 * Update contractor's payout method
 * @param payload
 * @returns {Promise}
 */
export function contractorPayoutChange(payload) {
  let data = stringify(payload, { arrayFormat: 'bracket' });
  if (payload instanceof FormData) data = payload;
  const id = payload instanceof FormData ? payload.get('id') : payload.id;
  return axios.post(CONTRACTOR_PAYOUT_TAX({ id }), data);
}

/**
 * add Contractor page
 * @param formData
 * @returns {Promise}
 */
export function addContractorPage(formData) {
  const id = formData.get('id');
  const headers = { 'Content-Type': 'multipart/form-data' };
  return axios.post(CONTRACTOR_PAGE({ id }), formData, { headers });
}

/**
 * update Contractor page co-branded
 * @param formData
 * @returns {Promise}
 */
export function updateContractorPage(formData) {
  const id = formData.get('id');
  const pid = formData.get('pId');
  const headers = { 'Content-Type': 'multipart/form-data' };
  return axios.post(CONTRACTOR_PAGE_UPDATE({ id, pid }), formData, { headers });
}
/**
 * update Contractor page marketing materials
 * @param formData
 * @returns {Promise}
 */
export function updateContractorPageMarketingMaterials(formData) {
  const id = formData.get('id');
  const pid = formData.get('pId');
  const headers = { 'Content-Type': 'multipart/form-data' };
  return axios.post(
    CONTRACTOR_PAGE_UPDATE_MARKETING_MATERIALS({ id, pid }), formData, { headers },
  );
}

/**
 * get Contractor page co-branded
 * @param id
 * @returns {Promise}
 */
export function getContractorPage(id) {
  return axios.get(CONTRACTOR_PAGE({ id }));
}

/**
 * get page settings
 * @returns {Promise}
 */
export function pageSettings() {
  return axios.get(CONTRACTOR_PAGE_SETTINGS);
}

/**
 * Update contractor request
 * @param payload
 * @returns {Promise}
 */
export function update(payload) {
  let data = stringify(payload, { arrayFormat: 'bracket' });
  if (payload instanceof FormData) data = payload;
  const id = payload instanceof FormData ? payload.get('id') : payload.id;
  const isManage = Object.prototype.hasOwnProperty.call(payload, 'isManage') ? payload.isManage : false;
  const headers = payload instanceof FormData
    ? { 'Content-Type': 'multipart/form-data' }
    : {};
  if (isManage) {
    return axios.post(MANAGE_CONTRACTOR_URL({ id }), data, { headers });
  }
  return axios.post(CONTRACTOR_URL({ id }), data, { headers });
}

/**
 * Get contractor referral url by id
 * @param id
 * @returns {Promise}
 */
export function referralUrl(id) {
  return axios.get(REFERRAL_URL({ id }));
}

export function setQuiz(params) {
  const id = params.get('contractorId');
  const headers = { 'Content-Type': 'multipart/form-data' };
  return axios.post(CONTRACTOR_QUIZ({ id }), params, { headers });
}
/**
 * Get contractor's co-branded pages analytics
 * @param id
 * @param start Date string formatted to m/d/Y
 * @param end Date string formatted to m/d/Y
 * @returns {Promise}
 */
export function coBrandedAnalytics(id, start, end) {
  return axios.get(
    CONTRACTOR_CO_BRANDED_ANALYTICS({ id }),
    {
      params: {
        filters: {
          lastEditDate: {
            start,
            end,
          },
        },
      },
    },
  );
}

export function teamMax(id) {
  return axios.get(CONTRACTOR_TEAM_MAX({ id }));
}

export function teamUsers(id) {
  return axios.get(CONTRACTOR_TEAM_USERS({ id }));
}

export function teamResources(id) {
  return axios.get(CONTRACTOR_TEAM_RESOURCES({ id }));
}

export function teamUserCreate(contractorId, params) {
  return axios.post(CONTRACTOR_TEAM_USER_CREATE({ contractorId }), params);
}

export function teamUserUpdate(contractorId, userId, params) {
  return axios.post(CONTRACTOR_TEAM_USER_UPDATE({ contractorId, userId }), params);
}

export function teamUserDelete(contractorId, userId) {
  return axios.delete(CONTRACTOR_TEAM_USER_DELETE({ contractorId, userId }));
}

export function contractorsConnectedAccount(contractorId) {
  return axios.get(CONTRACTOR_CONNECTED_ACCOUNT({ contractorId }));
}

export function contractorCreateConnectedAccount(contractorId) {
  return axios.post(CONTRACTOR_CONNECTED_ACCOUNT({ contractorId }));
}
export function contractorRefreshConnectedAccount(contractorId) {
  return axios.post(CONTRACTOR_REFRESH_CONNECTED_ACCOUNT({ contractorId }));
}

export function contractorSuccessConnectedAccount(contractorId) {
  return axios.post(CONTRACTOR_SUCCESS_CONNECTED_ACCOUNT({ contractorId }));
}
